import React from 'react';

import classNames from 'classnames';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { OptionProps } from 'react-select/src/components/Option';

import Chip from '@material-ui/core/Chip';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import { withStyles, createStyles, WithStyles, Theme } from '@material-ui/core/styles';
import { emphasize } from '@material-ui/core/styles/colorManipulator';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

import { ICON_NAMES, MaterialDesignIcon } from '../util/material-design-icons';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
    },
    input: {
      display: 'flex',
      padding: 0,
      height: '100%',
      minHeight: '3em',
    },
    valueContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      flex: 1,
      alignItems: 'center',
      overflow: 'hidden',
      maxHeight: '100px',
    },
    chip: {
      margin: `4px 2px`,
    },
    chipFocused: {
      backgroundColor: emphasize(
        theme.palette.type === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
        0.08,
      ),
    },
    noOptionsMessage: {
      padding: `8px 16px`,
    },
    singleValue: {
      fontSize: 16,
    },
    placeholder: {
      position: 'absolute',
      left: 2,
      fontSize: 16,
    },
    paper: {
      position: 'absolute',
      zIndex: 1,
      marginTop: '8px',
      left: 0,
      right: 0,
    },
  });

// https://react-select.com/components

const NoOptionsMessage = (props: OptionProps<any>) => (
  <Typography
    color="textSecondary"
    className={props.selectProps.classes.noOptionsMessage}
    {...props.innerProps}
  >
    {props.children}
  </Typography>
);

const inputComponent = (props: OptionProps<any> & { inputRef: React.Ref<any> }) => {
  const { inputRef, ...newProps } = props;
  return <div ref={newProps.innerRef} {...newProps} />;
};

const Control = (props: OptionProps<any>) => (
  <TextField
    fullWidth
    InputProps={{
      inputComponent,
      inputProps: {
        className: props.selectProps.classes.input,
        children: props.children,
        ...props.innerProps,
      },
    }}
    {...props.selectProps.textFieldProps}
  />
);

const Option = (props: OptionProps<any>) => (
  <MenuItem
    buttonRef={props.innerRef}
    selected={props.isFocused}
    component="div"
    style={{
      fontWeight: 400,
    }}
    {...props.innerProps}
  >
    {props.children}
  </MenuItem>
);

const Placeholder = (props: OptionProps<any>) => (
  <Typography
    color="textSecondary"
    className={props.selectProps.classes.placeholder}
    {...props.innerProps}
  >
    {props.children}
  </Typography>
);

const SingleValue = (props: OptionProps<any>) => (
  <Typography className={props.selectProps.classes.singleValue} {...props.innerProps}>
    {props.children}
  </Typography>
);

const ValueContainer = (props: OptionProps<any>) => (
  <div className={props.selectProps.classes.valueContainer}>{props.children}</div>
);

const MultiValue = (props: any) => (
  <Chip
    tabIndex={-1}
    label={props.children}
    className={classNames(props.selectProps.classes.chip, {
      [props.selectProps.classes.chipFocused]: props.isFocused,
    })}
    onDelete={props.removeProps.onClick}
    deleteIcon={<MaterialDesignIcon iconName={ICON_NAMES.CANCEL} {...props.removeProps} />}
  />
);

const Menu = (props: OptionProps<any>) => (
  <Paper square className={props.selectProps.classes.paper} {...props.innerProps}>
    {props.children}
  </Paper>
);

const components: any = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
};

interface ReactSelectorValue {
  label: string;
  value: string;
}

interface Props extends WithStyles<typeof styles> {
  options: Array<ReactSelectorValue>;
  value: ReactSelectorValue | Array<ReactSelectorValue> | null;
  onChange: (value: any, actionMeta: any) => void;
  label?: string;
  placeholder?: string;
  formatCreateLabel?: (inputValue: string) => React.ReactNode;
  isValidNewOption?: (inputValue: any, selectValue: any, selectOptions: any) => boolean;
  theme: Theme;
  autofocus?: boolean;
}

// Legacy - remove in PROD-4039
class MaterialReactSelectComponent extends React.PureComponent<Props> {
  render() {
    const { classes, theme, options, value, onChange, label, placeholder, autofocus } = this.props;

    const selectStyles = {
      input: (base: any) => ({
        ...base,
        color: theme.palette.text.primary,
        '& input': {
          font: 'inherit',
        },
      }),
      clearIndicator: (base: any) => ({
        ...base,
        cursor: 'pointer',
      }),
      dropdownIndicator: (base: any) => ({
        ...base,
        cursor: 'pointer',
      }),
    };

    return (
      <div className={classes.root} data-cy="custom-selector">
        <Select
          classes={classes}
          styles={selectStyles}
          textFieldProps={{
            label,
            InputLabelProps: {
              shrink: true,
            },
          }}
          options={options}
          components={components}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          isMulti
          ref={(input) => {
            if (input && autofocus) {
              input.focus();
            }
          }}
        />
      </div>
    );
  }
}

export const MaterialReactSelect = withStyles(styles, { withTheme: true })(
  MaterialReactSelectComponent,
);

class MaterialReactCreatableSelectComponent extends React.PureComponent<Props> {
  render() {
    const {
      classes,
      theme,
      options,
      value,
      onChange,
      label,
      placeholder,
      formatCreateLabel,
      isValidNewOption,
      autofocus,
    } = this.props;

    const selectStyles = {
      input: (base: any) => ({
        ...base,
        color: theme.palette.text.primary,
        '& input': {
          font: 'inherit',
        },
      }),
      clearIndicator: (base: any) => ({
        ...base,
        cursor: 'pointer',
      }),
      dropdownIndicator: (base: any) => ({
        ...base,
        cursor: 'pointer',
      }),
    };

    return (
      <div className={classes.root}>
        <CreatableSelect
          allowCreateWhileLoading={false}
          classes={classes}
          styles={selectStyles}
          textFieldProps={{
            label,
            InputLabelProps: {
              shrink: true,
            },
          }}
          options={options}
          components={components}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          isMulti
          formatCreateLabel={formatCreateLabel}
          isValidNewOption={isValidNewOption}
          ref={(input) => {
            if (input && autofocus) {
              input.focus();
            }
          }}
        />
      </div>
    );
  }
}

export const MaterialReactCreatableSelect = withStyles(styles, { withTheme: true })(
  MaterialReactCreatableSelectComponent,
);
